import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useHolds } from "@/components/use-holds";
import { Pill } from "@/components/ui/pill";
import { errorText } from "@/components/ui/states";
import { useUiStore } from "@/stores/ui-store";
import { ConnectionStatus } from "./connection-check";
import { ConnectionDialog } from "./connection-dialog";
import { useCheckConnection } from "./hooks";
import type { Connection } from "./types";

export function ConnectionRow({
  connection: c,
  connectorLabel,
  kind,
}: {
  connection: Connection;
  connectorLabel: string;
  kind?: "agent" | "traces" | undefined;
}) {
  const check = useCheckConnection();
  const notify = useUiStore((s) => s.notify);
  const [open, setOpen] = useState<"edit" | null>(null);
  const admin = useHolds("connections:write");
  const runCheck = () =>
    check.mutate(c.id, { onError: (e) => notify(errorText(e, "Could not check the connection."), "error") });
  return (
    <tr>
      <td>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-sm font-semibold">{c.name}</span>
          <span className="src">{c.base_url}</span>
        </div>
      </td>
      <td>
        <div className="flex flex-wrap items-center gap-1.5">
          <Pill>{connectorLabel}</Pill>
          {kind && (
            <span className="text-xs text-mute">{kind === "traces" ? "Trace source" : "Agent platform"}</span>
          )}
        </div>
      </td>
      <td aria-live="polite">
        <ConnectionStatus connection={c} checking={check.isPending} />
      </td>
      <td>
        {/* Checking signs in with the connection's secret: an admin action, like editing it. */}
        {admin && (
          <div className="flex items-center justify-end gap-1.5">
            <Button size="sm" disabled={check.isPending} onClick={runCheck} aria-label={`Check ${c.name}`}>
              Check
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen("edit")} aria-label={`Edit ${c.name}`}>
              Edit
            </Button>
          </div>
        )}
        {admin && open === "edit" && <ConnectionDialog connection={c} onClose={() => setOpen(null)} />}
      </td>
    </tr>
  );
}
